import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Grid, Card, CardContent,
    LinearProgress, Box, Divider, Chip, Radio,
    RadioGroup, FormControlLabel, IconButton, Tooltip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Close as CloseIcon, Info as InfoIcon } from '@material-ui/icons';
import HttpService from '../../services/httpService';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    giftCard: {
        cursor: 'pointer',
        transition: 'all 0.3s',
        border: '2px solid transparent',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
        }
    },
    selectedGift: {
        border: '2px solid #4caf50',
        backgroundColor: '#e8f5e9'
    },
    pointsBadge: {
        background: 'linear-gradient(135deg, #1c375d, #2e5a8a)',
        color: '#fff',
        padding: '12px 24px',
        borderRadius: '12px',
        textAlign: 'center'
    },
    progressBar: {
        height: 10,
        borderRadius: 5,
        backgroundColor: '#e0e0e0',
        '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(90deg, #fdb913, #f47e46)',
            borderRadius: 5
        }
    }
}));

const RedemptionModal = ({ open, onClose, userPoints, userAchievementPct, userId }) => {
    const classes = useStyles();
    const [giftItems, setGiftItems] = useState(null);
    const [selectedGift, setSelectedGift] = useState(null);
    const [loading, setLoading] = useState(false);
    const [redeemPercentage, setRedeemPercentage] = useState(0);
    const [redeemablePoints, setRedeemablePoints] = useState(0);
    const [redemptionHistory, setRedemptionHistory] = useState([]);

    useEffect(() => {
        if (open && userPoints) {
            fetchGiftItems();
            fetchRedemptionHistory();
            calculateRedeemablePoints();
        }
    }, [open, userPoints]);

    const fetchGiftItems = async () => {
        setLoading(true);
        try {
            const response = await HttpService.getGiftItems(userPoints);
            if (response?.data?.success) {
                setGiftItems(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching gift items:', error);
        }
        setLoading(false);
    };

    const fetchRedemptionHistory = async () => {
        try {
            const response = await HttpService.getRedemptionHistory(userId);
            if (response?.data?.success) {
                setRedemptionHistory(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    const calculateRedeemablePoints = () => {
        // Calculate based on achievement percentage
        if (userAchievementPct >= 91) {
            setRedeemPercentage(100);
            setRedeemablePoints(userPoints);
        } else if (userAchievementPct >= 81) {
            setRedeemPercentage(90);
            setRedeemablePoints(Math.floor(userPoints * 0.9));
        } else if (userAchievementPct >= 71) {
            setRedeemPercentage(75);
            setRedeemablePoints(Math.floor(userPoints * 0.75));
        } else if (userAchievementPct >= 65) {
            setRedeemPercentage(50);
            setRedeemablePoints(Math.floor(userPoints * 0.5));
        } else {
            setRedeemPercentage(0);
            setRedeemablePoints(0);
        }
    };

    const handleRedeem = async () => {
        if (!selectedGift) {
            alert('Please select a gift item');
            return;
        }

        setLoading(true);
        try {
            const response = await HttpService.requestRedemption({
                pointsToRedeem: selectedGift.minPoints,
                giftItemId: selectedGift.minPoints,
                giftAmount: selectedGift.amount,
                giftDescription: selectedGift.items.join(', ')
            });

            if (response?.data?.success) {
                alert('Redemption request submitted successfully!');
                onClose();
            } else {
                alert(response?.data?.message || 'Redemption failed');
            }
        } catch (error) {
            console.error('Error redeeming:', error);
            alert(error?.response?.data?.message || 'Error processing redemption');
        }
        setLoading(false);
    };

    const isEligibleForRedemption = () => {
        const now = new Date();
        const isDecember = now.getMonth() === 11;
        const hasPoints = redeemablePoints > 0;
        const minimumPoints = giftItems?.allGifts?.[0]?.minPoints || 75000;
        const meetsMinimumPoints = userPoints >= minimumPoints;

        const reasons = [];
        if (!isDecember) reasons.push('Redemption window is only open in December');
        if (!hasPoints) reasons.push('No redeemable points available based on achievement %');
        if (!meetsMinimumPoints) reasons.push(`Minimum ${minimumPoints.toLocaleString()} points required for any gift`);

        return {
            eligible: isDecember && hasPoints && meetsMinimumPoints,
            reasons
        };
    };

    const eligibility = isEligibleForRedemption();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>🎁 Redeem Your Rewards</span>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent dividers>
                {/* Points Summary */}
                <Box className={classes.pointsBadge} mb={3}>
                    <Typography variant="h6">Your Points Balance</Typography>
                    <Typography variant="h3" style={{ fontWeight: 'bold' }}>
                        {userPoints?.toLocaleString()}
                    </Typography>
                    <Typography variant="body2">
                        Redeemable: {redeemablePoints?.toLocaleString()} points ({redeemPercentage}%)
                    </Typography>
                    <Box mt={2}>
                        <LinearProgress 
                            variant="determinate" 
                            value={(userPoints / 300000) * 100} 
                            className={classes.progressBar}
                        />
                        <Typography variant="caption" style={{ marginTop: '8px', display: 'block' }}>
                            Next milestone: 300,000 points for Gold Coin
                        </Typography>
                    </Box>
                </Box>

                {/* Eligibility Check */}
                {!eligibility.eligible && (
                    <Alert severity="warning" style={{ marginBottom: '16px' }}>
                        <strong>Not eligible for redemption:</strong>
                        <ul style={{ margin: '8px 0 0 20px' }}>
                            {eligibility.reasons.map((reason, idx) => (
                                <li key={idx}>{reason}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {/* Next Tier Info */}
                {giftItems?.nextTier && (
                    <Alert severity="info" style={{ marginBottom: '16px' }}>
                        <strong>🎯 You're almost there!</strong> Need {giftItems.nextTier.pointsNeeded.toLocaleString()} more points to reach 
                        {giftItems.nextTier.items.join(', ')} (₹{giftItems.nextTier.amount.toLocaleString()})
                    </Alert>
                )}

                <Divider style={{ margin: '16px 0' }} />

                {/* Gift Items */}
                <Typography variant="h6" gutterBottom>
                    Available Gifts
                    <Tooltip title="Based on your total points (not redeemable points)">
                        <InfoIcon style={{ fontSize: '16px', marginLeft: '8px', cursor: 'pointer', color: '#888' }} />
                    </Tooltip>
                </Typography>

                <Grid container spacing={2}>
                    {giftItems?.allGifts?.map((gift, index) => {
                        const isReachable = userPoints >= gift.minPoints;
                        const isSelected = selectedGift?.minPoints === gift.minPoints;

                        return (
                            <Grid item xs={12} sm={6} key={index}>
                                <Card 
                                    className={`${classes.giftCard} ${isSelected ? classes.selectedGift : ''}`}
                                    style={{ opacity: isReachable ? 1 : 0.5 }}
                                    onClick={() => isReachable && setSelectedGift(gift)}
                                >
                                    <CardContent>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" style={{ color: '#1c375d' }}>
                                                ₹{gift.amount?.toLocaleString()}
                                            </Typography>
                                            <Chip 
                                                label={`${gift.minPoints?.toLocaleString()}+ points`}
                                                size="small"
                                                style={{ backgroundColor: '#fdb913', color: '#1c375d', fontWeight: 'bold' }}
                                            />
                                        </div>
                                        <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                                            {gift.items?.join(' • ')}
                                        </Typography>
                                        {isReachable && userPoints >= gift.minPoints && (
                                            <Radio
                                                checked={isSelected}
                                                onChange={() => setSelectedGift(gift)}
                                                style={{ marginTop: '8px' }}
                                            />
                                        )}
                                        {!isReachable && (
                                            <Typography variant="caption" color="error" style={{ marginTop: '8px', display: 'block' }}>
                                                Need {Math.abs(gift.minPoints - userPoints).toLocaleString()} more points
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Redemption History */}
                {redemptionHistory.length > 0 && (
                    <>
                        <Divider style={{ margin: '24px 0 16px' }} />
                        <Typography variant="h6" gutterBottom>Redemption History</Typography>
                        <Box maxHeight={200} overflow="auto">
                            {redemptionHistory.map((record, idx) => (
                                <Card key={idx} style={{ marginBottom: '8px' }}>
                                    <CardContent>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div>
                                                <Typography variant="body2">
                                                    <strong>Points Redeemed:</strong> {record.pointsRedeemed?.toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Gift:</strong> {record.giftDescription || `₹${record.giftAmount?.toLocaleString()}`}
                                                </Typography>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <Chip 
                                                    label={record.status}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: record.status === 'Delivered' ? '#4caf50' : '#ff9800',
                                                        color: '#fff'
                                                    }}
                                                />
                                                <Typography variant="caption" display="block">
                                                    {new Date(record.requestedAt).toLocaleDateString()}
                                                </Typography>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="default">
                    Cancel
                </Button>
                <Button
                    onClick={handleRedeem}
                    variant="contained"
                    style={{ backgroundColor: '#4caf50', color: '#fff' }}
                    disabled={!eligibility.eligible || !selectedGift || loading}
                >
                    {loading ? 'Processing...' : 'Redeem Now'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RedemptionModal;